import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Button, Card, CardBody, CardHeader, Col, Row, Spinner } from 'reactstrap'
import { IBackground } from './index'
import { toast } from 'react-toastify'
import { getAssetUrl } from '../../../helpers/backend_helper'
import DeleteModal from 'Components/Common/DeleteModal'
import { useDebouncedCallback } from 'use-debounce'

interface IProps {
  fetching: boolean
  backgroundRecords: IBackground[]
  handleOpenDetails: (arg: string) => void
  setOpenBackgroundPopup: (arg: boolean) => void
  setEditMode: (arg: boolean) => void
  handleDeleteBackground: (arg: string) => Promise<string>
  handleRenameCategory: (oldName: string, newName: string) => Promise<void>
  categories: { _id: string; name: string }[]
}

const BackgroundsTable: React.FC<IProps> = ({
  backgroundRecords,
  handleOpenDetails,
  setOpenBackgroundPopup,
  setEditMode,
  fetching,
  handleDeleteBackground,
  handleRenameCategory,
  categories
}) => {
  const [showModalDelete, setShowModalDelete] = useState<boolean>(false)
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined)
  const [query, setQuery] = useState('')
  const [filteredRecords, setFilteredRecords] = useState<IBackground[]>(backgroundRecords)
  // Kategori düzenleme
  const [editingCat, setEditingCat] = useState<string | null>(null)
  const [editCatValue, setEditCatValue] = useState('')

  useEffect(() => {
    setFilteredRecords(backgroundRecords)
  }, [backgroundRecords])

  const handleAddBackground = () => {
    setEditMode(false)
    setOpenBackgroundPopup(true)
  }

  const handleEditBackground = (id: string | undefined) => {
    if (id) {
      setEditMode(true)
      setOpenBackgroundPopup(true)
      handleOpenDetails(id)
    }
  }

  const handleDeleteRecord = async (id: string | undefined) => {
    if (id) {
      const message = await handleDeleteBackground(id)
      if (message === 'SUCCESSFULL') {
        toast.success('Arka plan başarıyla silindi')
      }
    }
  }

  const handleShowModalDelete = (id: string | undefined) => {
    if (id) {
      setShowModalDelete(true)
      setSelectedId(id)
    }
  }

  const handleDelete = () => {
    handleDeleteRecord(selectedId)
    setShowModalDelete(false)
  }

  const debouncedSearch = useDebouncedCallback((searchTerm: string) => {
    if (!searchTerm.trim()) {
      setFilteredRecords(backgroundRecords)
    } else {
      const filtered = backgroundRecords.filter((bg) =>
        bg.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        bg.category.toLowerCase().includes(searchTerm.toLowerCase())
      )
      setFilteredRecords(filtered)
    }
  }, 300)

  const handleInputChange = (e: { target: { value: string } }) => {
    const value = e.target.value
    setQuery(value)
    debouncedSearch(value)
  }

  return (
    <React.Fragment>
      <Row>
        <Col xl={12}>
          <Card>
            <CardHeader className='align-items-center d-flex justify-content-between'>
              <div className='search-box d-inline-block col-8 d-flex align-items-center'>
                <input
                  id='search-bar-background'
                  className='form-control search'
                  placeholder='Arka plan veya kategori ara...'
                  onChange={handleInputChange}
                  value={query}
                />
                <i className='bx bx-search-alt search-icon'></i>
              </div>
              <Button className='p-2' color='primary' onClick={handleAddBackground}>
                <div className='flex align-items-center'>
                  {fetching ? (
                    <Spinner size='sm'> Loading... </Spinner>
                  ) : (
                    <>
                      <i className='mdi mdi-plus'></i>
                      <span className='fw-semibold ms-1'>Yeni Arka Plan</span>
                    </>
                  )}
                </div>
              </Button>
            </CardHeader>
            {/* Kategori Yönetimi */}
            {categories.length > 0 && (
              <div className='px-3 py-2 border-bottom bg-light'>
                <small className='text-muted fw-semibold d-block mb-1'>Kategoriler:</small>
                <div className='d-flex flex-wrap gap-2 align-items-center'>
                  {categories.map((cat) => (
                    <div key={cat._id} className='d-flex align-items-center gap-1'>
                      {editingCat === cat.name ? (
                        <>
                          <input
                            className='form-control form-control-sm'
                            style={{ width: 120 }}
                            value={editCatValue}
                            onChange={(e) => setEditCatValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && editCatValue.trim() && editCatValue.trim() !== cat.name) {
                                handleRenameCategory(cat.name, editCatValue.trim())
                                setEditingCat(null)
                              }
                              if (e.key === 'Escape') setEditingCat(null)
                            }}
                            autoFocus
                          />
                          <Button size='sm' color='success' className='p-0 px-1' onClick={() => {
                            if (editCatValue.trim() && editCatValue.trim() !== cat.name) {
                              handleRenameCategory(cat.name, editCatValue.trim())
                            }
                            setEditingCat(null)
                          }}>✓</Button>
                          <Button size='sm' color='light' className='p-0 px-1' onClick={() => setEditingCat(null)}>✕</Button>
                        </>
                      ) : (
                        <span
                          className='badge bg-primary-subtle text-primary cursor-pointer d-flex align-items-center gap-1'
                          style={{ cursor: 'pointer' }}
                          onClick={() => { setEditingCat(cat.name); setEditCatValue(cat.name) }}
                          title='Düzenlemek için tıklayın'
                        >
                          {cat.name}
                          <i className='mdi mdi-pencil' style={{ fontSize: 10 }}></i>
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <CardBody>
              <div className='table-responsive table-card'>
                <table className='table align-middle table-nowrap table-striped-columns mb-0'>
                  <thead className='table-light'>
                    <tr>
                      <th scope='col' style={{ width: '80px' }}>Önizleme</th>
                      <th scope='col'>İsim</th>
                      <th scope='col'>Kategori</th>
                      <th scope='col'>Aktif Booth Sayısı</th>
                      <th scope='col' style={{ width: '150px' }}>İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRecords.map((background, index) => (
                      <tr key={index}>
                        <td>
                          <img
                            src={getAssetUrl(background.imageLink)}
                            alt={background.name}
                            style={{ width: '60px', height: '40px', objectFit: 'cover', borderRadius: '4px' }}
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = 'https://placehold.co/60x40?text=No+Image'
                            }}
                          />
                        </td>
                        <td>
                          <Link to='#' className='fw-medium'>
                            {background.name}
                          </Link>
                        </td>
                        <td>{background.category}</td>
                        <td>
                          <span className='badge bg-info'>
                            {background.isActiveFor?.length || 0} booth
                          </span>
                        </td>
                        <td>
                          <button
                            type='button'
                            className='btn btn-sm btn-light'
                            onClick={() => handleEditBackground(background?._id)}
                          >
                            Düzenle
                          </button>
                          <button
                            type='button'
                            className='btn btn-sm btn-danger ms-2'
                            onClick={() => handleShowModalDelete(background?._id)}
                          >
                            Sil
                          </button>
                        </td>
                      </tr>
                    ))}
                    {filteredRecords.length === 0 && !fetching && (
                      <tr>
                        <td colSpan={5} className='text-center py-4'>
                          Arka plan bulunamadı
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
      <DeleteModal
        show={showModalDelete}
        onDeleteClick={handleDelete}
        onCloseClick={() => setShowModalDelete(false)}
      />
    </React.Fragment>
  )
}

export default BackgroundsTable
